import { type UseQueryOptions, useQuery, useQueryClient } from "@tanstack/react-query"
import { arrayMoveImmutable } from "array-move"
import { useCallback, useMemo } from "react"

import {
  type DenormalizedTargetAttribute,
  type DenormalizedTemplate,
  type TargetAttribute,
  type Template,
  getViewDataTemplates,
} from "@/api/templates"
import { queryKeys } from "@/queries/keys"
import { logEvent } from "@/util/telemetry"

// Templates are seeded into the query cache from bootstrap viewData by
// `hydrateQueriesFromViewData` (non-slim) or by the deferred fetcher behind the
// `DeferredViewDataGate` (slim). Both populate `queryKeys.templates.all` before
// any consumer mounts, so the hydrated data is authoritative and mutations keep
// the cache current instead of refetching on every mount. The queryFn is only a
// fallback for render paths that mount without hydrated data.
//
// The cache stores the denormalized shape viewData ships (`targetAttributes`
// nested per template). Consumers read the normalized `Template` (with
// `targetAttributeIds`) and a flat `TargetAttribute` lookup the former Redux
// `templatesData` slice produced; `useNormalizedTemplates` derives both.
const EMPTY_TEMPLATES: readonly DenormalizedTemplate[] = []
const EMPTY_TARGET_ATTRIBUTES: readonly (TargetAttribute | undefined)[] = []

const templatesQueryOptions: UseQueryOptions<
  DenormalizedTemplate[],
  Error,
  DenormalizedTemplate[],
  typeof queryKeys.templates.all
> = {
  queryKey: queryKeys.templates.all,
  async queryFn() {
    try {
      const { data: templates } = await getViewDataTemplates()
      return templates
    } catch (error) {
      logEvent("error", "Failed to load templates", {
        capture: true,
        error: error instanceof Error ? error : new Error(String(error)),
      })
      throw error
    }
  },
  retry: false,
  staleTime: Infinity,
}

interface NormalizedTemplates {
  allTemplates: Template[]
  templatesById: Record<number, Template>
  targetAttributesById: Record<number, TargetAttribute>
}

// Keyed on the cache array reference so the normalized snapshot is computed once
// per data version and shared across every consumer. React Query hands back the
// same array reference until a cache writer replaces it, so consumers keep
// stable object identities between renders (no re-render storms across the ~47
// selector call sites).
const normalizedByListRef = new WeakMap<
  readonly DenormalizedTemplate[],
  NormalizedTemplates
>()

function normalizeTemplates(list: readonly DenormalizedTemplate[]): NormalizedTemplates {
  const cached = normalizedByListRef.get(list)
  if (cached) {
    return cached
  }

  const templatesById: Record<number, Template> = {}
  const targetAttributesById: Record<number, TargetAttribute> = {}
  const allTemplates = list.map((denormalized) => {
    const { targetAttributes, ...rest } = denormalized
    const targetAttributeIds = (targetAttributes ?? []).map((ta) => {
      targetAttributesById[ta.id] = ta
      return ta.id
    })
    const template: Template = { ...rest, targetAttributeIds }
    templatesById[template.id] = template
    return template
  })

  const normalized = { allTemplates, templatesById, targetAttributesById }
  normalizedByListRef.set(list, normalized)
  return normalized
}

function useTemplatesData(): readonly DenormalizedTemplate[] {
  const { data } = useQuery(templatesQueryOptions)
  return data ?? EMPTY_TEMPLATES
}

function useNormalizedTemplates(): NormalizedTemplates {
  const list = useTemplatesData()
  return useMemo(() => normalizeTemplates(list), [list])
}

export const useAllTemplates = (): Template[] => useNormalizedTemplates().allTemplates

export function useAllTemplatesWithStatus(): {
  allTemplates: Template[]
  isLoading: boolean
  isError: boolean
  retry: () => void
} {
  const { data, isPending, isError, isFetching, refetch } =
    useQuery(templatesQueryOptions)

  const allTemplates = useMemo(
    () => normalizeTemplates(data ?? EMPTY_TEMPLATES).allTemplates,
    [data],
  )

  const retry = useCallback(() => {
    void refetch()
  }, [refetch])

  return {
    allTemplates,
    isLoading: isPending || (isError && isFetching),
    isError: isError && !isFetching,
    retry,
  }
}

export const useTemplates = (): Record<number, Template> =>
  useNormalizedTemplates().templatesById

export const useTemplateById = (id?: number): Template | undefined => {
  const { templatesById } = useNormalizedTemplates()
  return id !== undefined ? templatesById[id] : undefined
}

export const useTargetAttributes = (): Record<number, TargetAttribute> =>
  useNormalizedTemplates().targetAttributesById

export const useTargetAttributesByIds = (
  ids: number[],
): (TargetAttribute | undefined)[] => {
  const { targetAttributesById } = useNormalizedTemplates()
  return useMemo(() => {
    if (ids.length === 0) {
      return EMPTY_TARGET_ATTRIBUTES as (TargetAttribute | undefined)[]
    }
    return ids.map((id) => targetAttributesById[id])
  }, [targetAttributesById, ids])
}

interface TemplateAndTargetAttributes {
  template: Template
  targetAttributes: (TargetAttribute | undefined)[]
}

export function useTemplateAndTargetAttributesById(
  templateId?: number,
): Partial<TemplateAndTargetAttributes>
export function useTemplateAndTargetAttributesById(
  templateId: number,
): TemplateAndTargetAttributes
export function useTemplateAndTargetAttributesById(
  templateId?: number,
): Partial<TemplateAndTargetAttributes> {
  const template = useTemplateById(templateId)
  const targetAttributes = useTargetAttributesByIds(
    template ? template.targetAttributeIds : [],
  )
  return { template, targetAttributes }
}

/**
 * Cache writers for template + target-attribute mutations. Call sites update
 * the `templates` query cache directly so the shared list stays current without
 * a refetch. Writers operate on the denormalized shape (nested
 * `targetAttributes`) stored in the cache.
 *
 * Deleting a template and updating/deleting a target attribute additionally
 * dispatch `deleteTemplateById` / `updateTargetAttribute` /
 * `deleteTargetAttributeById` at the call site so the `lists` Redux slice can
 * cascade those changes into its list attributes; that domain has not been
 * migrated to React Query yet.
 */
export function useTemplatesCache() {
  const queryClient = useQueryClient()

  // `queryClient` is stable, so these writers keep a stable identity across
  // renders and are safe to list in effect/`useAsync` dependency arrays.
  const writeList = useCallback(
    (updater: (previous: DenormalizedTemplate[]) => DenormalizedTemplate[]) => {
      queryClient.setQueryData<DenormalizedTemplate[]>(
        queryKeys.templates.all,
        (previous) => updater(previous ?? []),
      )
    },
    [queryClient],
  )

  const upsertTemplate = useCallback(
    (template: DenormalizedTemplate) => {
      writeList((templates) => {
        const index = templates.findIndex((t) => t.id === template.id)
        if (index === -1) {
          return [...templates, template]
        }
        const next = templates.slice()
        // PUT /api/templates/:id packs metadata only (no `targetAttributes`), so
        // preserve the existing column list/ordering when the response omits it.
        next[index] =
          template.targetAttributes == null
            ? { ...template, targetAttributes: next[index].targetAttributes }
            : template
        return next
      })
    },
    [writeList],
  )

  const removeTemplate = useCallback(
    (id: number) => {
      writeList((templates) => templates.filter((t) => t.id !== id))
    },
    [writeList],
  )

  const addTargetAttribute = useCallback(
    (templateId: number, targetAttribute: DenormalizedTargetAttribute) => {
      writeList((templates) =>
        templates.map((template) =>
          template.id === templateId
            ? {
                ...template,
                targetAttributes: [...template.targetAttributes, targetAttribute],
              }
            : template,
        ),
      )
    },
    [writeList],
  )

  const updateTargetAttribute = useCallback(
    (targetAttribute: DenormalizedTargetAttribute) => {
      writeList((templates) =>
        templates.map((template) => {
          if (!template.targetAttributes.some((ta) => ta.id === targetAttribute.id)) {
            return template
          }
          return {
            ...template,
            targetAttributes: template.targetAttributes.map((ta) =>
              ta.id === targetAttribute.id ? targetAttribute : ta,
            ),
          }
        }),
      )
    },
    [writeList],
  )

  const removeTargetAttribute = useCallback(
    (templateId: number, targetAttributeId: number) => {
      writeList((templates) =>
        templates.map((template) =>
          template.id === templateId
            ? {
                ...template,
                targetAttributes: template.targetAttributes.filter(
                  (ta) => ta.id !== targetAttributeId,
                ),
              }
            : template,
        ),
      )
    },
    [writeList],
  )

  const reorderTargetAttributes = useCallback(
    (templateId: number, oldIndex: number, newIndex: number) => {
      writeList((templates) =>
        templates.map((template) => {
          if (template.id !== templateId) {
            return template
          }
          const reordered = arrayMoveImmutable(
            template.targetAttributes,
            oldIndex,
            newIndex,
          ).map((ta, index) => ({ ...ta, index }))
          return { ...template, targetAttributes: reordered }
        }),
      )
    },
    [writeList],
  )

  return useMemo(
    () => ({
      upsertTemplate,
      removeTemplate,
      addTargetAttribute,
      updateTargetAttribute,
      removeTargetAttribute,
      reorderTargetAttributes,
    }),
    [
      upsertTemplate,
      removeTemplate,
      addTargetAttribute,
      updateTargetAttribute,
      removeTargetAttribute,
      reorderTargetAttributes,
    ],
  )
}
